import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import type {
  AgentOption,
  CannedReplyView,
  ChatMessageView,
  ConversationStatus,
  ConversationView,
  Page,
} from '@vmc/shared';
import { api, errorMessage, isApiError } from '@/lib/api';
import { useUser } from '@/lib/auth';
import { patchQuery } from '@/lib/optimistic';
import { toast } from 'sonner';

export type InboxTab = 'queued' | 'mine' | 'open';

export const chatKeys = {
  all: ['conversations'] as const,
  list: (tab: InboxTab) => ['conversations', 'list', tab] as const,
  queuedCount: ['conversations', 'queued-count'] as const,
  detail: (id: string) => ['conversations', 'detail', id] as const,
  messages: (id: string) => ['conversations', 'messages', id] as const,
  canned: ['canned-replies'] as const,
  agents: ['admin-users', 'agents'] as const,
};

const TAB_QUERY: Record<InboxTab, { status?: ConversationStatus; mine?: string }> = {
  queued: { status: 'QUEUED' },
  mine: { mine: 'true' },
  // No status → the API returns QUEUED + ASSIGNED.
  open: {},
};

export function useConversations(tab: InboxTab) {
  return useInfiniteQuery({
    queryKey: chatKeys.list(tab),
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<ConversationView>>(
        '/api/conversations',
        { ...TAB_QUERY[tab], cursor: pageParam, limit: 50 },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 15_000,
  });
}

const COUNT_CAP = 100;

/**
 * Sidebar badge. There's no count endpoint, so fetch one page of queued conversations;
 * anything past the cap shows as "99+".
 */
export function useQueuedCount(enabled: boolean) {
  return useQuery({
    queryKey: chatKeys.queuedCount,
    queryFn: ({ signal }) =>
      api.get<Page<ConversationView>>(
        '/api/conversations',
        { status: 'QUEUED', limit: COUNT_CAP },
        signal,
      ),
    select: (page) => ({ count: page.items.length, more: page.nextCursor !== null }),
    enabled,
    staleTime: 30_000,
    // Realtime events invalidate this; the interval is a fallback if the socket is down.
    refetchInterval: 60_000,
    retry: false,
  });
}

export function useConversation(id: string | undefined) {
  return useQuery({
    queryKey: chatKeys.detail(id ?? ''),
    queryFn: ({ signal }) =>
      api.get<ConversationView>(`/api/conversations/${id}`, undefined, signal),
    enabled: !!id,
    staleTime: 10_000,
  });
}

const PAGE_SIZE = 50;

/** Pages go backwards in time: each page is older than the previous one. */
export function useConversationMessages(id: string | undefined) {
  return useInfiniteQuery({
    queryKey: chatKeys.messages(id ?? ''),
    queryFn: ({ pageParam, signal }) =>
      api.get<ChatMessageView[]>(
        `/api/conversations/${id}/messages`,
        { before: pageParam, limit: PAGE_SIZE },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.length < PAGE_SIZE ? undefined : last[0]?.createdAt),
    enabled: !!id,
    staleTime: 60_000,
  });
}

/** Appends a message to the cached thread (from a reply or a realtime event), skipping duplicates. */
export function appendMessage(
  qc: QueryClient,
  conversationId: string,
  message: ChatMessageView,
): void {
  qc.setQueryData<InfiniteData<ChatMessageView[], string | undefined>>(
    chatKeys.messages(conversationId),
    (data) => {
      if (!data) return data;
      if (data.pages.some((p) => p.some((m) => m.id === message.id))) return data;
      const [newest = [], ...older] = data.pages;
      return { ...data, pages: [[...newest, message], ...older] };
    },
  );
}

function useConversationAction<TVars = void>(
  path: (vars: TVars) => { url: string; body?: unknown },
  success: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: TVars) => {
      const { url, body } = path(vars);
      return api.post<ConversationView>(url, body);
    },
    onSuccess: (conv) => {
      qc.setQueryData(chatKeys.detail(conv.id), conv);
      void qc.invalidateQueries({ queryKey: ['conversations', 'list'] });
      void qc.invalidateQueries({ queryKey: chatKeys.queuedCount });
    },
    meta: { success },
  });
}

/**
 * Claim (DESIGN.md rule 2): the chat shows as yours at once; a failed claim (someone else got it
 * first, network) rolls the detail back and the mutation cache toasts the error.
 */
export function useClaim(id: string) {
  const qc = useQueryClient();
  const user = useUser();
  return useMutation({
    mutationFn: () => api.post<ConversationView>(`/api/conversations/${id}/claim`),
    onMutate: async () => ({
      rollback: await patchQuery<ConversationView>(
        qc,
        chatKeys.detail(id),
        (v) => v && { ...v, status: 'ASSIGNED', assignedAgent: { id: user.id, name: user.name } },
      ),
    }),
    onError: (_e, _v, ctx) => ctx?.rollback(),
    onSuccess: (conv) => {
      qc.setQueryData(chatKeys.detail(conv.id), conv);
      void qc.invalidateQueries({ queryKey: ['conversations', 'list'] });
      void qc.invalidateQueries({ queryKey: chatKeys.queuedCount });
    },
    meta: { success: 'Chat claimed' },
  });
}
export const useCloseConversation = (id: string) =>
  useConversationAction(() => ({ url: `/api/conversations/${id}/close` }), 'Chat closed');

const STATUS_RANK: Record<ChatMessageView['status'], number> = {
  QUEUED: 0,
  RECEIVED: 0,
  SENT: 1,
  DELIVERED: 2,
  READ: 3,
  FAILED: 4,
};

/** Apply a delivery receipt from realtime; receipts can arrive out of order, so never go backwards. */
export function setMessageStatus(
  qc: QueryClient,
  conversationId: string,
  id: string,
  status: ChatMessageView['status'],
): void {
  updateMessage(qc, conversationId, id, (m) =>
    STATUS_RANK[status] > STATUS_RANK[m.status] ? { ...m, status } : m,
  );
}

/**
 * Replaces (or patches, or with `null` removes) one message in the cached thread, e.g. a temp
 * message once the server confirms it.
 */
export function updateMessage(
  qc: QueryClient,
  conversationId: string,
  id: string,
  next: ChatMessageView | ((m: ChatMessageView) => ChatMessageView) | null,
): void {
  qc.setQueryData<InfiniteData<ChatMessageView[], string | undefined>>(
    chatKeys.messages(conversationId),
    (data) => {
      if (!data) return data;
      const pages = data.pages.map((page) =>
        page.flatMap((m) => {
          if (m.id !== id) return [m];
          if (next === null) return [];
          return [typeof next === 'function' ? next(m) : next];
        }),
      );
      // If the realtime copy already arrived, drop the duplicate the swap would create.
      const seen = new Set<string>();
      return {
        ...data,
        pages: pages.map((page) =>
          page.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true))),
        ),
      };
    },
  );
}

/**
 * Optimistic agent reply (DESIGN.md rule 2): the message appears instantly with a "sending"
 * clock, then swaps to the server's copy (same id the realtime event carries). On failure it
 * stays in the thread marked FAILED with a Retry button on the bubble; retrying calls this again
 * with the same `tempId`, which flips the failed message back to "sending" instead of adding a
 * second copy.
 */
export function useReply(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ text }: { text: string; tempId: string }) =>
      api.post<ChatMessageView>(`/api/conversations/${id}/reply`, { text }),
    onMutate: ({ text, tempId }) => {
      // A retry: the failed copy is already in the thread.
      updateMessage(qc, id, tempId, (m) => ({ ...m, status: 'QUEUED' }));
      appendMessage(qc, id, {
        id: tempId,
        direction: 'OUTBOUND',
        senderType: 'AGENT',
        type: 'TEXT',
        body: text,
        bodyEn: null,
        mediaKey: null,
        status: 'QUEUED',
        createdAt: new Date().toISOString(),
      });
    },
    onSuccess: (message, { tempId }) => {
      updateMessage(qc, id, tempId, message);
      void qc.invalidateQueries({ queryKey: chatKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: ['conversations', 'list'] });
    },
    onError: (e, { tempId }) => {
      updateMessage(qc, id, tempId, (m) => ({ ...m, status: 'FAILED' }));
      if (isApiError(e) && e.errorCode === 'WINDOW_CLOSED') {
        // The composer swaps itself for the window-closed explanation; refresh `windowOpen`.
        void qc.invalidateQueries({ queryKey: chatKeys.detail(id) });
      } else {
        toast.error(errorMessage(e));
      }
    },
    // Toasted above, except WINDOW_CLOSED, which the composer explains inline.
    meta: { errorToast: false },
  });
}

/**
 * Plain requests for assign and close, without the mutation cache's success toast: the page sends
 * them after its Undo toast's window passes (rule 5).
 */
export function assignConversation(id: string, agentId: string) {
  return api.post<ConversationView>(`/api/conversations/${id}/assign`, { agentId });
}

export function closeConversation(id: string) {
  return api.post<ConversationView>(`/api/conversations/${id}/close`);
}

export function useCannedReplies(enabled = true) {
  return useQuery({
    queryKey: chatKeys.canned,
    queryFn: ({ signal }) => api.get<CannedReplyView[]>('/api/canned-replies', undefined, signal),
    staleTime: 5 * 60_000,
    enabled,
    retry: false,
  });
}

export function useAgents(enabled = true) {
  return useQuery({
    queryKey: chatKeys.agents,
    queryFn: ({ signal }) => api.get<AgentOption[]>('/api/admin-users/agents', undefined, signal),
    staleTime: 5 * 60_000,
    enabled,
  });
}

/** Voice notes and attachments need the bearer header, so they're fetched as Blobs. */
export function useMedia(key: string | null) {
  return useQuery({
    queryKey: ['media', key],
    queryFn: ({ signal }) =>
      api.blob(`/api/media/${(key ?? '').split('/').map(encodeURIComponent).join('/')}`, signal),
    enabled: !!key,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    retry: false,
  });
}
